import { hide, show } from "../../../utils";
import { fromEvent } from "rxjs";
import { debounceTime, filter, first, pluck } from "rxjs/operators";
import { IAddressLookupService } from "../../../services/interfaces/addressLookupService";
import { SessionDto, AddressSearchResultsItemDto } from "@se/platform";
import { ILoggingService } from "../../../services/interfaces/loggingService";
import { AddressComponent } from "../addressComponent";

class SearchView {

    private searchboxEl: HTMLInputElement;
    private resultsEl: HTMLElement;
    private resultsSummaryEl: HTMLElement;
    private resultsCountEl: HTMLElement;
    private resultsCountDescEl: HTMLElement;
    private resultsTemplateEl: HTMLTemplateElement;
    private resultsListEl: HTMLElement;
    private notFoundButtonEl: HTMLElement;
    private helpNotFoundButtonEl: NodeListOf<HTMLElement>;

    constructor(private viewEl: HTMLElement, private component: AddressComponent,
        private session: SessionDto, private addressLookupService: IAddressLookupService, private appInsights: ILoggingService,) {

        this.searchboxEl = viewEl.querySelector<HTMLInputElement>(".js-searchbox");

        this.resultsEl = viewEl.querySelector<HTMLElement>(".js-results");
        this.resultsSummaryEl = viewEl.querySelector<HTMLElement>(".js-results-summary");
        this.resultsCountEl = viewEl.querySelector<HTMLElement>(".js-results-count");
        this.resultsCountDescEl = viewEl.querySelector<HTMLElement>(".js-results-count-desc");
        this.resultsTemplateEl = viewEl.querySelector<HTMLTemplateElement>(".js-results-template");
        this.resultsListEl = viewEl.querySelector<HTMLElement>(".js-results-list");
        this.notFoundButtonEl = viewEl.querySelector<HTMLElement>(".js-not-found");
        this.helpNotFoundButtonEl = viewEl.querySelectorAll<HTMLElement>(".js-help-not-found");

        const onSearchTermChanged$ = fromEvent<HTMLInputElement>(this.searchboxEl, "input").pipe(
            debounceTime(300),
            pluck("target", "value"),
        );

        onSearchTermChanged$.subscribe((searchTerm:string) => {
            hide(this.resultsEl);
            this.resultsEl.setAttribute("aria-expanded", "false");
            if (searchTerm.length > 3) {
                this.search(searchTerm);
            }
            
        });

        const onNotFoundClicked$ = fromEvent([this.notFoundButtonEl, ...this.helpNotFoundButtonEl], "click").pipe();
        onNotFoundClicked$.subscribe(_ => this.component.enterManualAddress());

        const onKeydown$ = fromEvent<KeyboardEvent>(document, "keydown").pipe(
            filter((e: KeyboardEvent) => { 
                const watchedKeys = ["Enter", "ArrowUp", "ArrowDown", "Escape", "Tab"];
                return this.viewEl.contains(document.activeElement) && watchedKeys.includes(e.key) 
            })
        );
        onKeydown$.subscribe(event => this.handleKeyboardNavigation(event));
    }

    private handleKeyboardNavigation(event: KeyboardEvent) {
        
        const currentItemEl = event.target as HTMLElement;

        // Exit/hide results on certain Tab events
        if (event.key === "Tab") {
            if (event.shiftKey && currentItemEl === this.searchboxEl) {
                this.exitResultsList();
            } else if (!event.shiftKey) {
                if (currentItemEl === this.resultsListEl.lastElementChild || (currentItemEl === this.notFoundButtonEl && this.resultsListEl.childElementCount < 1)) {
                    this.exitResultsList();
                }
            }
        }

        if (event.key !== "Tab") {
            // Prevents native form submission on Enter and browser scrolls on Up/Down keys
            event.preventDefault();
        }

        // Handle key events
        if (event.key === "ArrowUp" || event.key === "ArrowDown") {
            if (event.key === "ArrowUp") {
                if (currentItemEl === this.resultsListEl.firstElementChild) {
                    this.notFoundButtonEl.focus();
                } else {
                    (currentItemEl.previousElementSibling as HTMLElement)?.focus();
                }
            } 
            else if (event.key === "ArrowDown") {
                if (currentItemEl === this.searchboxEl) {
                    this.notFoundButtonEl.focus();
                } else if (currentItemEl === this.notFoundButtonEl) {
                    (this.resultsListEl.firstElementChild as HTMLElement)?.focus();
                } else {
                    (currentItemEl.nextElementSibling as HTMLElement)?.focus();
                }
            }
        } 
        else if (event.key === "Enter") {
            const currentItemEl = event.target as HTMLElement;
            currentItemEl.click();
        }
        else if (event.key === "Escape") {
            this.exitResultsList();
        }
    }

    private async search(searchTerm: string) {

        this.clearResultsList();
        this.resultsCountEl.innerText = "";
        this.resultsCountDescEl.innerText = "Searching...";
        show(this.resultsSummaryEl);
        this.resultsEl.setAttribute("aria-expanded", "true");
        show(this.resultsEl);

        try {
            const response = await this.addressLookupService.searchAddresses(this.session, searchTerm);

            let resultsCountMessage = ""; // Gets read aloud by assistive tech
            let resultsCountDescMessage = ""; // Does not get read aloud by assistive tech
            if (response.wasSuccessful) {
                const addressresults = response.content.results.filter(address => address.isContainer == false);
                const totalResults = addressresults.length;
                addressresults.forEach((searchResult, index) => this.addResultToView(searchResult, index));

                if (totalResults == 1) {
                    resultsCountMessage = `Showing ${totalResults} result. `;
                }
                else if (totalResults > 1) {
                    resultsCountMessage = `Showing ${totalResults} results. `;
                    resultsCountDescMessage = `Keep typing your address to refine results.`;
                }
                else {
                    resultsCountMessage = `No results found. `;
                    resultsCountDescMessage = `Try entering a different part of your address.`;
                }
            }
            else {
                resultsCountMessage = `Sorry, there was an error processing your search. Please try again.`;
                resultsCountDescMessage = ``;
            }
            this.resultsCountEl.innerText = resultsCountMessage;
            this.resultsCountDescEl.innerText = resultsCountDescMessage;
        } catch (exception) {
            this.appInsights.LogException("SearchView-search addressLookupService searchAddresses " + exception);
            this.resultsCountEl.innerText = `Sorry, there was an error processing your search. Please try again.`;
            this.resultsCountDescEl.innerText = ``;
        }
    }

    private addResultToView(searchResult: AddressSearchResultsItemDto, index: number) {

        // use the template to add a new result to the list
        const template = document.importNode(this.resultsTemplateEl, true);
        const searchResultItemEl = template.content.querySelector<HTMLLIElement>(".js-item");
        searchResultItemEl.innerText = searchResult.label;
        searchResultItemEl.dataset.index = index.toString();
        if (searchResult.isContainer) {
            searchResultItemEl.classList.add("address-lookup__result-item--is-container");
        }

        this.resultsListEl.appendChild(searchResultItemEl);

        // add a listner to handle the address being selected by the user
        const onResultSelected$ = fromEvent<HTMLLIElement>(searchResultItemEl, "click").pipe(first());
        onResultSelected$.subscribe(c => {
            this.component.selectAddress(searchResult.id);
            this.clearResultsList();
            this.resultsEl.setAttribute("aria-expanded", "false");
        });
    }

    public show() {
        show(this.viewEl);
        this.searchboxEl.value = "";
        this.resultsEl.setAttribute("aria-expanded", "false");
        hide(this.resultsEl);
    }

    public focus() {
        this.searchboxEl.focus();
    }

    public hide() {
        hide(this.viewEl);
    }

    public clearResultsList() {
        this.resultsListEl.innerHTML = "";
    }

    public exitResultsList() {
        this.component.resetSearch();
        this.resultsCountEl.innerHTML = "";
        this.resultsCountDescEl.innerHTML = "";
        this.clearResultsList();
    }
}

export { SearchView }